/**
 * VUKZ — 9 Cenários Financeiros (3 investimento × 3 operacional)
 * Base editável para projeções de 5 anos
 * Versão 1.0 — Maio 2026
 */

// ─── Equity / Dilution ──────────────────────────────────────────────────────
export const INVESTMENT_SCENARIOS = {
  zero: {
    id: 'zero',
    label: 'Sem Investimento',
    amount: 0,
    equityGiven: 0,
    preMoneyValuation: null,
    postMoneyValuation: null,
    founderOwnership: 100,
    notes: 'Bootstrap. Crescimento 100% orgânico.',
  },
  seed_250k: {
    id: 'seed_250k',
    label: 'Seed R$ 1.3M (~USD 250K)',
    amount: 1300000,
    equityGiven: 10, // %
    preMoneyValuation: 11700000, // R$ 11.7M
    postMoneyValuation: 13000000, // R$ 13M
    founderOwnership: 90,
    notes: 'Valuation pre-money R$ 11.7M. Investidor recebe 10%. Equipe (2 devs + 1 comercial) + go-to-market + GPU infra.',
  },
  seed_500k: {
    id: 'seed_500k',
    label: 'Seed R$ 1.3M + Series A R$ 8M',
    amount: 9300000,
    equityGiven: 30, // % total
    preMoneyValuation: 11700000, // R$ 11.7M (Seed) → Series A at R$ 30M+
    postMoneyValuation: 40000000, // R$ 40M post Series A
    founderOwnership: 70,
    notes: 'Seed 10% (R$ 1.3M) + Series A 20% (R$ 8M em 2028). Equipe 20 pessoas, LATAM, GPU clusters, AI marketplace.',
  },
};

// ─── Premissas Comuns ───────────────────────────────────────────────────────
export const COMMON_ASSUMPTIONS = {
  currency: 'BRL',
  azureCreditsUSD: 250000,
  azureCreditsBurnMonths: 24, // estimativa de duração dos créditos
  taxOnRevenue: 0.0933, // Simples Nacional faixa 2-3
  annualInflation: 0.06,
  annualPriceIncrease: 0.08,
  paymentTermDays: 30,
  contractMinMonths: 6,
};

// ─── 9 Cenários ─────────────────────────────────────────────────────────────

export const FINANCIAL_SCENARIOS = [
  // ═══════════════════════════════════════════════════════════════════════════
  // SEM INVESTIMENTO
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'zero_pessimista',
    investment: 'zero',
    operational: 'pessimista',
    label: '0 + Pessimista',
    color: '#dc2626',
    summary: 'Sobrevivência. Produto evolui lento, poucos pilotos, sem equipe.',
    strategy: 'Foco total em 1-2 pilotos pagos. Sem marketing. Founder faz tudo.',
    team: '1 pessoa (founder)',
    focusProducts: ['VUKZ Coach Solo', 'SwimVision básico'],
    yearly: [
      { year: 2026, clients: 1, mrr: 799, arr: 9588, costs: 2500, result: -1701, runway: 'Indefinido (sem burn externo)' },
      { year: 2027, clients: 3, mrr: 2397, arr: 28764, costs: 3500, result: -1103, runway: 'Caixa apertado' },
      { year: 2028, clients: 6, mrr: 5500, arr: 66000, costs: 5000, result: 500, runway: 'Break-even' },
      { year: 2029, clients: 12, mrr: 12000, arr: 144000, costs: 7000, result: 5000, runway: 'Positivo' },
      { year: 2030, clients: 20, mrr: 22000, arr: 264000, costs: 10000, result: 12000, runway: 'Positivo' },
    ],
  },
  {
    id: 'zero_normal',
    investment: 'zero',
    operational: 'normal',
    label: '0 + Normal',
    color: '#d97706',
    summary: 'Validação gradual. Pilotos convertem, receita começa devagar.',
    strategy: 'Founder-led sales. 3-5 pilotos no ano 1. CoachR + Arena Stats.',
    team: '1 pessoa + freelancer pontual',
    focusProducts: ['VUKZ Club Start', 'SwimVision', 'TrackVision (ano 2)'],
    yearly: [
      { year: 2026, clients: 3, mrr: 1990, arr: 23880, costs: 3000, result: -1010, runway: 'Suportável' },
      { year: 2027, clients: 8, mrr: 8000, arr: 96000, costs: 5000, result: 3000, runway: 'Positivo' },
      { year: 2028, clients: 18, mrr: 22000, arr: 264000, costs: 8000, result: 14000, runway: 'Crescendo' },
      { year: 2029, clients: 35, mrr: 50000, arr: 600000, costs: 15000, result: 35000, runway: 'Saudável' },
      { year: 2030, clients: 55, mrr: 85000, arr: 1020000, costs: 25000, result: 60000, runway: 'Forte' },
    ],
  },
  {
    id: 'zero_otimista',
    investment: 'zero',
    operational: 'otimista',
    label: '0 + Otimista',
    color: '#16a34a',
    summary: 'Cliente âncora fecha rápido. Receita financia crescimento.',
    strategy: 'Clube relevante fecha no H2/2026. Upsell natural. Indicações.',
    team: '1 pessoa → 2 pessoas (ano 2, financiado pela receita)',
    focusProducts: ['VUKZ Club Performance', 'SwimVision', 'TrackVision (ano 2)'],
    yearly: [
      { year: 2026, clients: 5, mrr: 4990, arr: 59880, costs: 3500, result: 1490, runway: 'Positivo' },
      { year: 2027, clients: 15, mrr: 18000, arr: 216000, costs: 8000, result: 10000, runway: 'Contratação possível' },
      { year: 2028, clients: 35, mrr: 55000, arr: 660000, costs: 18000, result: 37000, runway: 'Forte' },
      { year: 2029, clients: 60, mrr: 100000, arr: 1200000, costs: 30000, result: 70000, runway: 'Muito forte' },
      { year: 2030, clients: 100, mrr: 180000, arr: 2160000, costs: 50000, result: 130000, runway: 'Líder regional' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // R$ 250K
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '250k_pessimista',
    investment: 'seed_250k',
    operational: 'pessimista',
    label: 'Seed + Pessimista',
    color: '#dc2626',
    summary: 'Investimento consumido rápido. Vendas lentas. Precisa nova rodada ou pivotar.',
    strategy: 'Contrata 2 devs + comercial. Produto melhora mas ciclo de venda é longo. Runway 10-12 meses.',
    team: 'Founder + 2 devs + 1 comercial',
    focusProducts: ['VUKZ Club Start', 'SwimVision', 'TrackVision'],
    yearly: [
      { year: 2026, clients: 2, mrr: 1200, arr: 14400, costs: 25000, result: -23800, runway: '10 meses' },
      { year: 2027, clients: 5, mrr: 5000, arr: 60000, costs: 20000, result: -15000, runway: 'Precisa receita ou nova rodada' },
      { year: 2028, clients: 10, mrr: 12000, arr: 144000, costs: 15000, result: -3000, runway: 'Quase break-even' },
      { year: 2029, clients: 20, mrr: 25000, arr: 300000, costs: 18000, result: 7000, runway: 'Positivo' },
      { year: 2030, clients: 35, mrr: 45000, arr: 540000, costs: 22000, result: 23000, runway: 'Estável' },
    ],
  },
  {
    id: '250k_normal',
    investment: 'seed_250k',
    operational: 'normal',
    label: 'Seed + Normal',
    color: '#d97706',
    summary: 'Produto vendável. 5-8 pilotos no ano 1. Receita cobre operação no ano 2.',
    strategy: 'Equipe entrega. SwimVision + TrackVision como diferencial. Pilotos convertem.',
    team: 'Founder + 2 devs + 1 comercial',
    focusProducts: ['VUKZ Club Performance', 'SwimVision', 'TrackVision (ano 2)'],
    yearly: [
      { year: 2026, clients: 5, mrr: 4000, arr: 48000, costs: 25000, result: -21000, runway: '10 meses' },
      { year: 2027, clients: 15, mrr: 18000, arr: 216000, costs: 22000, result: -4000, runway: 'Receita quase cobre' },
      { year: 2028, clients: 30, mrr: 45000, arr: 540000, costs: 28000, result: 17000, runway: 'Positivo' },
      { year: 2029, clients: 55, mrr: 90000, arr: 1080000, costs: 40000, result: 50000, runway: 'Forte' },
      { year: 2030, clients: 90, mrr: 160000, arr: 1920000, costs: 60000, result: 100000, runway: 'Muito forte' },
    ],
  },
  {
    id: '250k_otimista',
    investment: 'seed_250k',
    operational: 'otimista',
    label: 'Seed + Otimista',
    color: '#16a34a',
    summary: 'Parceria forte no H2/2026. Federação fecha. Visão computacional diferencia. Expansão rápida.',
    strategy: 'Clube grande ou federação fecha rápido. CV diferencia. Novos esportes em 2027.',
    team: 'Founder + 2 devs + 1 comercial + 1 CS',
    focusProducts: ['VUKZ Club Elite', 'SwimVision', 'TrackVision', 'Ginástica/Vôlei (ano 2)'],
    yearly: [
      { year: 2026, clients: 8, mrr: 8000, arr: 96000, costs: 25000, result: -17000, runway: '12+ meses' },
      { year: 2027, clients: 25, mrr: 40000, arr: 480000, costs: 30000, result: 10000, runway: 'Positivo' },
      { year: 2028, clients: 50, mrr: 90000, arr: 1080000, costs: 45000, result: 45000, runway: 'Series A ready' },
      { year: 2029, clients: 100, mrr: 180000, arr: 2160000, costs: 70000, result: 110000, runway: 'Líder' },
      { year: 2030, clients: 180, mrr: 350000, arr: 4200000, costs: 120000, result: 230000, runway: 'Expansão int.' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // R$ 500K
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '500k_pessimista',
    investment: 'seed_500k',
    operational: 'pessimista',
    label: 'Seed+A + Pessimista',
    color: '#dc2626',
    summary: 'Equipe contratada mas vendas lentas. Burn alto. Series A não fecha no timing ideal.',
    strategy: 'Produto bom, comercial fraco. Ciclo de venda longo em clubes. Precisa pivotar ou cortar.',
    team: 'Founder + 3 devs + 2 comercial + 1 CS',
    focusProducts: ['VUKZ Club Performance', 'SwimVision', 'TrackVision'],
    yearly: [
      { year: 2026, clients: 3, mrr: 3000, arr: 36000, costs: 40000, result: -37000, runway: '13 meses' },
      { year: 2027, clients: 10, mrr: 15000, arr: 180000, costs: 35000, result: -20000, runway: 'Precisa tração' },
      { year: 2028, clients: 22, mrr: 35000, arr: 420000, costs: 30000, result: 5000, runway: 'Break-even' },
      { year: 2029, clients: 40, mrr: 65000, arr: 780000, costs: 35000, result: 30000, runway: 'Estável' },
      { year: 2030, clients: 65, mrr: 110000, arr: 1320000, costs: 50000, result: 60000, runway: 'Crescendo' },
    ],
  },
  {
    id: '500k_normal',
    investment: 'seed_500k',
    operational: 'normal',
    label: 'Seed+A + Normal',
    color: '#d97706',
    summary: 'Cenário mais provável com Seed + Series A. Tração real, multi-esporte, ARR >R$ 1M no ano 3.',
    strategy: 'Equipe entrega, pilotos convertem, ticket sobe com upsell. LATAM em 2029.',
    team: 'Founder + 4 devs + 2 comercial + 1 CS + 1 designer',
    focusProducts: ['VUKZ Club Elite', 'SwimVision', 'TrackVision', 'Pipeline Academia (ano 3)'],
    yearly: [
      { year: 2026, clients: 8, mrr: 10000, arr: 120000, costs: 40000, result: -30000, runway: '14 meses' },
      { year: 2027, clients: 25, mrr: 40000, arr: 480000, costs: 38000, result: 2000, runway: 'Break-even' },
      { year: 2028, clients: 50, mrr: 85000, arr: 1020000, costs: 50000, result: 35000, runway: 'Series A ready' },
      { year: 2029, clients: 90, mrr: 160000, arr: 1920000, costs: 70000, result: 90000, runway: 'Forte' },
      { year: 2030, clients: 150, mrr: 300000, arr: 3600000, costs: 110000, result: 190000, runway: 'Líder BR' },
    ],
  },
  {
    id: '500k_otimista',
    investment: 'seed_500k',
    operational: 'otimista',
    label: 'Seed+A + Otimista',
    color: '#16a34a',
    summary: 'Tudo dá certo. Parcerias fortes, federações fecham, AI marketplace decola. LATAM em 2028.',
    strategy: 'Clubes relevantes + federação + AI agents no Microsoft Store. Expansão LATAM acelerada.',
    team: 'Founder + 5 devs + 3 comercial + 2 CS + design',
    focusProducts: ['VUKZ Complete', 'SwimVision', 'TrackVision', 'AI Agents Marketplace', 'Pipeline Academia'],
    yearly: [
      { year: 2026, clients: 12, mrr: 18000, arr: 216000, costs: 42000, result: -24000, runway: '15+ meses' },
      { year: 2027, clients: 40, mrr: 70000, arr: 840000, costs: 50000, result: 20000, runway: 'Positivo + captar Series A' },
      { year: 2028, clients: 80, mrr: 150000, arr: 1800000, costs: 80000, result: 70000, runway: 'Expansão int.' },
      { year: 2029, clients: 150, mrr: 300000, arr: 3600000, costs: 130000, result: 170000, runway: 'Líder LATAM' },
      { year: 2030, clients: 250, mrr: 550000, arr: 6600000, costs: 200000, result: 350000, runway: 'Series B / IPO prep' },
    ],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

export const getScenariosByInvestment = (investmentId) =>
  FINANCIAL_SCENARIOS.filter(s => s.investment === investmentId);

export const getScenarioById = (id) =>
  FINANCIAL_SCENARIOS.find(s => s.id === id);

export const getInvestmentLabel = (id) =>
  INVESTMENT_SCENARIOS[id]?.label || id;
